import React from 'react';

/**
 * Tech assignment dropdown. Used on service rows, ServiceItemsModal task rows,
 * and any "Assign all to…" headers. Wraps the same `<select>` pattern that was
 * inlined ~7× across requestimateState, ServiceItemsModal, and calendar.
 *
 * Emits the parsed integer id (or null when "Unassigned").
 *
 * Optional `onSplit` adds a "Split between techs…" entry at the end of the list
 * (only when 2+ techs are available). Selecting it fires `onSplit()` instead
 * of `onChange()` — used as the entry point to per-step assignment.
 */
const SPLIT_VALUE = '__split__';

const TechPicker = ({
  technicians = [],
  value,
  onChange,
  disabled = false,
  placeholder = 'Unassigned',
  size = 'sm',
  className = '',
  style,
  stopPropagation = true,
  ariaLabel,
  onSplit,
  splitLabel = 'Split between techs…',
}) => {
  const showSplit = typeof onSplit === 'function' && technicians.length >= 2;

  const handleChange = (e) => {
    const raw = e.target.value;
    if (raw === SPLIT_VALUE) {
      onSplit();
      return;
    }
    const parsed = raw === '' ? null : parseInt(raw, 10);
    onChange(parsed);
  };

  const handleClick = (e) => {
    if (stopPropagation) e.stopPropagation();
  };

  const sizeClass = size === 'sm' ? 'form-control-sm' : '';
  const minWidth = style && style.minWidth ? style.minWidth : '120px';

  return (
    <select
      aria-label={ariaLabel || 'Assign technician'}
      className={`form-control ${sizeClass} ${className}`.trim()}
      value={value === null || value === undefined ? '' : String(value)}
      onChange={handleChange}
      onClick={handleClick}
      disabled={disabled}
      style={{ minWidth, ...style }}
    >
      <option value="">{placeholder}</option>
      {technicians.map((tech) => {
        const id = tech.User_id ?? tech.user_id;
        return (
          <option key={id} value={id}>
            {tech.user_name}
          </option>
        );
      })}
      {showSplit && (
        <optgroup label="─────────────">
          <option value={SPLIT_VALUE}>{splitLabel}</option>
        </optgroup>
      )}
    </select>
  );
};

export default TechPicker;
